import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { GameHud } from '@/components/GameHud';
import { GlassButton } from '@/components/GlassButton';
import { GlassCard } from '@/components/GlassCard';
import { Icon } from '@/components/Icon';
import { TapToAsk } from '@/components/TapToAsk';
import { sfx } from '@/lib/audio';
import { speakingGrade, SPEAKING_THRESHOLDS } from '@/lib/score';
import { ModeSwitcher } from './ModeSwitcher';
import { StudyHeader } from './StudyHeader';
import { useStudyLesson } from './useStudyLesson';

interface SpeechRecognitionResult { transcript: string; confidence: number }
interface SpeechRecognitionEvent extends Event {
  results: { [index: number]: { [index: number]: SpeechRecognitionResult } & { isFinal: boolean } } & { length: number };
}
interface SpeechRecognition extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => void) | null;
  onend: ((this: SpeechRecognition, ev: Event) => void) | null;
  onerror: ((this: SpeechRecognition, ev: Event) => void) | null;
  start(): void;
  stop(): void;
}
declare global {
  interface Window {
    SpeechRecognition?: { new(): SpeechRecognition };
    webkitSpeechRecognition?: { new(): SpeechRecognition };
  }
}

function similarity(a: string, b: string): number {
  const aw = a.toLowerCase().replace(/[^a-z0-9' ]/g, '').split(/\s+/).filter(Boolean);
  const bw = b.toLowerCase().replace(/[^a-z0-9' ]/g, '').split(/\s+/).filter(Boolean);
  if (aw.length === 0 || bw.length === 0) return 0;
  const matched = aw.filter((w) => bw.includes(w)).length;
  return Math.round((matched / Math.max(aw.length, bw.length)) * 100);
}

export function SpeakingPage(): JSX.Element {
  const { ready, notFoundView, study } = useStudyLesson('speaking');
  if (!ready || study === null) return notFoundView ?? <div />;

  const { course, lesson, sentence, index, total, progressPct, setProgressBoost,
          next, submitGrade, markComplete, bumpStreak, bumpMastery, recordMistake } = study;

  const [recognized, setRecognized] = useState('');
  const [score, setScore] = useState<number | null>(null);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // peekVisible — UI toggle (show/hide answer)
  // peekedOnce  — sticky penalty: once peeked you can't get Perfect
  const [peekVisible, setPeekVisible] = useState(false);
  const [peekedOnce, setPeekedOnce] = useState(false);
  const startTimeRef = useRef<number>(0);
  const recogRef = useRef<SpeechRecognition | null>(null);

  useEffect(() => {
    setRecognized('');
    setScore(null);
    setError(null);
    setPeekVisible(false);
    setPeekedOnce(false);
  }, [sentence.id]);

  function togglePeek(): void {
    if (score !== null) return;
    setPeekVisible((v) => {
      if (!v) setPeekedOnce(true);
      return !v;
    });
  }

  useEffect(() => {
    const t = setTimeout(() => tts(sentence.en), 350);
    return () => {
      clearTimeout(t);
      if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    };
  }, [sentence.id, sentence.en]);

  function tts(text: string, rate = 0.95): void {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'en-US';
    u.rate = rate;
    window.speechSynthesis.speak(u);
  }

  function finalize(text: string): void {
    const s = similarity(text, sentence.en);
    setScore(s);
    const g = speakingGrade(s);
    // Peeked attempts can't be Perfect — cap at Great.
    submitGrade(peekedOnce && g === 'perfect' ? 'great' : g);
    if (s >= SPEAKING_THRESHOLDS.good) {
      setProgressBoost(true);
      markComplete();
      bumpStreak();
      if (!peekedOnce && (g === 'perfect' || g === 'great')) bumpMastery();
    } else {
      recordMistake({ errorType: 'pronunciation', userInput: text });
    }
  }

  function startRecord(): void {
    setError(null);
    const Ctor = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (Ctor === undefined) {
      setError('当前浏览器不支持语音识别 — 请用 Chrome 桌面版');
      return;
    }
    const recog = new Ctor();
    recog.lang = 'en-US';
    recog.continuous = false;
    recog.interimResults = false;
    recog.onresult = (e) => {
      const r = e.results[0]?.[0];
      if (r === undefined) return;
      setRecognized(r.transcript);
      finalize(r.transcript);
    };
    recog.onend = () => setRecording(false);
    recog.onerror = () => { setError('录音被中断 — 检查麦克风权限'); setRecording(false); };
    recogRef.current = recog;
    sfx.click();
    startTimeRef.current = Date.now();
    recog.start();
    setRecording(true);
  }

  function stopRecord(): void {
    recogRef.current?.stop();
    setRecording(false);
  }

  function reset(): void {
    setRecognized('');
    setScore(null);
    setError(null);
    setPeekVisible(false);
    setPeekedOnce(false);
  }

  // Always show answer after attempt; show toggle when peeked.
  const showAnswer = score !== null || peekVisible;

  return (
    <>
      <GameHud />
      <div className="space-y-8 scanlines relative pb-4">
        <StudyHeader
          courseId={course.id} courseTitle={course.title}
          lessonTitle={lesson.title} sentenceId={sentence.id}
          mode="speaking"
          index={index} total={total} progressPct={progressPct}
        />

        <GlassCard>
          <div className="text-xs uppercase tracking-[0.18em] text-honey-300 font-arcade mb-3">
            LISTEN · SHADOW · SPEAK IT OUT
          </div>
          <div className="text-lg md:text-2xl text-cream-100 mb-6 text-balance leading-relaxed">
            {sentence.zh}
          </div>

          <AnimatePresence mode="wait">
            {showAnswer ? (
              <motion.div
                key="answer"
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="font-mono text-xl md:text-2xl text-mint-200 text-balance leading-snug mb-2"
              >
                <TapToAsk sentence={sentence.en} tone="lime" />
                {peekedOnce && score === null && (
                  <div className="mt-1 text-[10px] text-ember-300/70 font-arcade tracking-widest">PEEK USED · PERFECT 已禁用</div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="hidden"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="rounded-2xl border border-dashed border-honey-400/30 bg-honey-400/[0.03] py-4 text-center text-sm text-cream-400 mb-2 font-arcade tracking-widest"
              >
                🔒 ENGLISH · 听完之后说出来
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-8 flex flex-col items-center gap-4">
            <div className="flex gap-3 items-center">
              <button
                onClick={() => tts(sentence.en)}
                aria-label="听一遍"
                className="group relative h-14 w-14 rounded-full bg-honey-400/15 border-2 border-honey-400/60 grid place-items-center hover:bg-honey-400/25 hover:scale-105 transition-all"
              >
                <Icon name="listen" size="lg" />
              </button>
              <button
                onClick={() => tts(sentence.en, 0.65)}
                aria-label="慢速重听"
                className="text-xs text-cream-400 hover:text-honey-300 transition-colors inline-flex items-center gap-1"
              >
                <Icon name="slow" size="sm" /> 慢速
              </button>
            </div>

            <button
              onClick={recording ? stopRecord : startRecord}
              aria-label={recording ? '停止录音' : '开始录音'}
              className={`relative h-28 w-28 rounded-full grid place-items-center transition-all text-ink-950 ${
                recording ? 'bg-ember-500 glow-pink'
                : 'bg-gradient-to-br from-honey-400 to-ember-500 glow-cyan hover:scale-105'
              }`}
            >
              <Icon name={recording ? 'square' : 'speaking'} size={40} strokeWidth={2.4} />
              {recording && (
                <>
                  <span className="absolute inset-0 rounded-full border-2 border-ember-300 animate-ping" />
                  <span className="absolute -inset-2 rounded-full border border-ember-400/50 animate-pulse" />
                </>
              )}
            </button>
            <div className="text-xs text-cream-400 font-arcade tracking-widest">
              {recording ? 'LISTENING…' : 'TAP TO SPEAK'}
            </div>

            <button
              onClick={togglePeek}
              disabled={score !== null}
              className="text-xs text-cream-400 hover:text-honey-300 disabled:opacity-40 font-arcade tracking-widest inline-flex items-center gap-1.5"
            >
              <Icon name="peek" size="sm" />
              {peekVisible ? 'HIDE · 收起答案' : 'PEEK · 偷看答案'}
              {peekedOnce && !peekVisible && (
                <span className="text-ember-300/70 ml-1">已扣 PERFECT</span>
              )}
            </button>
          </div>

          {error !== null && (
            <div className="mt-6 rounded-2xl bg-ember-500/[0.08] border border-ember-500/30 px-4 py-3 text-sm text-ember-200">
              ⚠ {error}
              <div className="mt-2 text-xs text-cream-400">
                没法用麦？
                <button
                  onClick={() => { setRecognized(sentence.en); finalize(sentence.en); }}
                  className="ml-2 underline text-honey-300 hover:text-honey-200"
                >
                  模拟一次完美发音
                </button>
              </div>
            </div>
          )}

          {recognized !== '' && score !== null && (
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="mt-6 rounded-2xl p-5 border bg-ink-800/60 border-white/[0.06]"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs text-cream-400 font-arcade tracking-widest">RECOGNIZED</div>
                  <div className="font-mono text-white mt-1 break-all">{recognized}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-cream-400 font-arcade tracking-widest">SCORE</div>
                  <div className={`mt-1 font-score text-5xl text-neon ${
                    score >= SPEAKING_THRESHOLDS.perfect ? 'text-honey-400'
                    : score >= SPEAKING_THRESHOLDS.good + 10 ? 'text-mint-400'
                    : 'text-ember-400'
                  }`}>{score}</div>
                </div>
              </div>
              <div className="mt-4 h-2 rounded-full bg-white/[0.06] overflow-hidden">
                <motion.div
                  initial={{ width: 0 }} animate={{ width: `${score}%` }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className={`h-full ${
                    score >= SPEAKING_THRESHOLDS.perfect ? 'bg-gradient-to-r from-honey-400 to-mint-400'
                    : score >= SPEAKING_THRESHOLDS.good + 10 ? 'bg-gradient-to-r from-mint-400 to-mint-500'
                    : 'bg-gradient-to-r from-ember-400 to-ember-500'
                  }`}
                />
              </div>
            </motion.div>
          )}
        </GlassCard>

        <AnimatePresence mode="wait">
          {score !== null && score >= SPEAKING_THRESHOLDS.good && (
            <motion.div
              key="next"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="rounded-3xl p-5 border bg-mint-500/[0.08] border-mint-500/30 flex items-center justify-between gap-4 flex-wrap"
            >
              <div className="font-arcade text-mint-300 tracking-widest text-xs">🎉 NEXT UP</div>
              <GlassButton onClick={next} size="md" variant="solid">
                {index + 1 < total ? 'NEXT →' : 'COMPLETE ✨'}
              </GlassButton>
            </motion.div>
          )}
        </AnimatePresence>

        <ModeSwitcher
          lessonId={lesson.id} current="speaking"
          extra={<button onClick={reset} className="text-cream-400 hover:text-honey-300 self-center ml-2">↺ RETRY</button>}
        />
      </div>
    </>
  );
}
